import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { type BaseSearchResponse } from '@modules/common/types/base-search-response';
import { type ConnectionsService } from '@modules/connections/services/connections.service';
import { type Connection } from '@modules/connections/types/connection';
import { type KnotsService } from '@modules/knots/services/knots.service';
import { type Knot } from '@modules/knots/types/knot';
import { type TagsService } from '@modules/tags/services/tags.service';
import { type Tag } from '@modules/tags/types/tag';
import { uniq } from 'lodash-es';
import { forkJoin, type Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/** The selected markers a neighbour query sends. */
export interface NeighborMarkers {
  readonly knots: Knot[];
  readonly tags: Tag[];
  readonly connections: Connection[];
}

/** The by-name lookups the three marker services already offer. */
export interface NeighborMarkerLookups {
  readonly knots: Pick<KnotsService, 'search'>;
  readonly tags: Pick<TagsService, 'search'>;
  readonly connections: Pick<ConnectionsService, 'search'>;
}

/**
 * The selection with an id on every marker: the neighbour endpoints filter by id, but a hub suggestion
 * selects a marker by name alone, so those are looked up by name in the query's tenant (SBUX-1173).
 * Emits `null` when none of the selection exists there, rather than falling back to the open item.
 */
export function resolveNeighborMarkers(
  markers: NeighborMarkers,
  lookups: NeighborMarkerLookups,
  config: StitchRequestConfig,
): Observable<NeighborMarkers | null> {
  const { knots, tags, connections } = markers;
  if (!hasNameOnly(knots) && !hasNameOnly(tags) && !hasNameOnly(connections)) {
    return of(markers);
  }

  return forkJoin({
    knots: withIds(knots, (names) => lookups.knots.search({ names, limit: names.length }, config)),
    tags: withIds(tags, (names) => lookups.tags.search({ names, limit: names.length }, config)),
    connections: withIds(connections, (names) => lookups.connections.search({ names, limit: names.length }, config)),
  }).pipe(map((resolved) => (countMarkers(resolved) === 0 && countMarkers(markers) > 0 ? null : resolved)));
}

function hasNameOnly(markers: ReadonlyArray<{ readonly id?: string }>): boolean {
  return markers.some(({ id }) => !id);
}

function countMarkers({ knots, tags, connections }: NeighborMarkers): number {
  return knots.length + tags.length + connections.length;
}

/** A failed lookup leaves its names unresolved; the service has already told the user. */
function withIds<T extends { readonly id?: string; readonly name: string }>(
  markers: T[],
  search: (names: string[]) => Observable<BaseSearchResponse<T>>,
): Observable<T[]> {
  const withId = markers.filter(({ id }) => !!id);
  const names = uniq(markers.filter(({ id }) => !id).map(({ name }) => name));
  if (!names.length) {
    return of(markers);
  }

  return search(names).pipe(
    map(({ items }) => [...withId, ...items]),
    catchError(() => of(withId)),
  );
}
